import type { ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import { formatAppArea } from "@/v2-backoffice-client/usage/usage.constants"

export type UsageAreaSegment = {
  Area: string
  Count: number
  Weighted?: number | null
}

export type UsageAreaBarProps = ComponentProps<"div"> & {
  /** Top interest areas, highest weighted volume first. */
  areas: UsageAreaSegment[]
}

// Rank-ordered opacities of a NEUTRAL slate: proportions matter here, not
// identity, and the leading area is already named in text. Keeps the row's
// color budget for the signals that encode meaning (green day strips, teal
// sparkline, amber/green product split).
const SEGMENT_OPACITY_CLASSES = [
  "bg-slate-400",
  "bg-slate-400/55",
  "bg-slate-400/30",
]

// Proportional stacked bar of a user's top interest areas with the leading
// area named next to it — replaces the comma-separated text column. Native
// `title` carries the full breakdown so table rows stay cheap.
function UsageAreaBar(props: UsageAreaBarProps) {
  const { areas, className, ...divProps } = props

  const weightOf = (area: UsageAreaSegment) => area.Weighted ?? area.Count
  const total = areas.reduce((sum, area) => sum + weightOf(area), 0)

  if (areas.length === 0 || total <= 0) {
    return (
      <span className="text-xs text-muted-foreground" {...divProps}>
        —
      </span>
    )
  }

  const label = areas
    .map(
      (area) =>
        `${formatAppArea(area.Area)} ${Math.round((weightOf(area) / total) * 100)}%`,
    )
    .join(" · ")

  return (
    <div
      className={cn("flex min-w-0 items-center gap-2", className)}
      title={label}
      {...divProps}
    >
      <div className="flex h-2 w-16 shrink-0 items-stretch overflow-hidden rounded-full bg-muted">
        {areas.map((area, i) => (
          <div
            key={area.Area}
            className={cn(
              SEGMENT_OPACITY_CLASSES[i] ?? "bg-slate-400/20",
              "min-w-[2px]",
            )}
            style={{ width: `${(weightOf(area) / total) * 100}%` }}
          />
        ))}
      </div>
      <span className="truncate text-xs text-muted-foreground">
        {formatAppArea(areas[0].Area)}
        {areas.length > 1 && ` +${areas.length - 1}`}
      </span>
    </div>
  )
}

export default UsageAreaBar
